import { useState, useRef, useEffect, useCallback } from 'react';

export interface UseAutocompleteOptions<T> {
  fetchResults: (query: string) => Promise<T[]>;
  onSelect: (item: T) => void;
  getLabel: (item: T) => string;
  initialItem?: T | null;
  minChars?: number;
  debounceMs?: number;
}

export function useAutocomplete<T>({
  fetchResults,
  onSelect,
  getLabel,
  minChars = 3,
  debounceMs = 300,
}: UseAutocompleteOptions<T>) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<T[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController>(new AbortController());

  // Click-outside detection
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && listRef.current) {
      const el = listRef.current.children[activeIndex] as HTMLElement | undefined;
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      abortControllerRef.current?.abort();
    };
  }, []);

  const performFetch = useCallback(
    async (searchQuery: string) => {
      abortControllerRef.current?.abort();
      const controller = new AbortController();
      abortControllerRef.current = controller;

      setIsLoading(true);
      try {
        const items = await fetchResults(searchQuery);
        if (!controller.signal.aborted) {
          setResults(items);
          setIsOpen(items.length > 0);
          setActiveIndex(-1);
        }
      } catch {
        if (!controller.signal.aborted) {
          setResults([]);
          setIsOpen(false);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    },
    [fetchResults],
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setQuery(value);

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

      if (value.length < minChars) {
        setResults([]);
        setIsOpen(false);
        setIsLoading(false);
        return;
      }

      debounceTimerRef.current = setTimeout(() => {
        performFetch(value);
      }, debounceMs);
    },
    [minChars, debounceMs, performFetch],
  );

  const selectItem = useCallback(
    (item: T) => {
      setQuery(getLabel(item));
      setIsOpen(false);
      setActiveIndex(-1);
      onSelect(item);
    },
    [getLabel, onSelect],
  );

  const clear = useCallback(() => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    setActiveIndex(-1);
    inputRef.current?.focus();
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setActiveIndex((prev) => (prev < results.length - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setActiveIndex((prev) => (prev > 0 ? prev - 1 : results.length - 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (activeIndex >= 0 && activeIndex < results.length) {
            selectItem(results[activeIndex]!);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          setActiveIndex(-1);
          break;
      }
    },
    [isOpen, results, activeIndex, selectItem],
  );

  const handleFocus = useCallback(() => {
    if (results.length > 0 && query.length >= minChars) {
      setIsOpen(true);
    }
  }, [results.length, query.length, minChars]);

  return {
    query,
    results,
    isOpen,
    isLoading,
    activeIndex,
    containerRef,
    inputRef,
    listRef,
    handleInputChange,
    handleKeyDown,
    handleFocus,
    selectItem,
    clear,
    setQuery,
    setResults,
    setIsOpen,
    setActiveIndex,
  };
}
